/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import ReactMarkdown from 'react-markdown';
import { ref, push, onValue, update, set } from 'firebase/database';
import { database, progressAPI } from '../firebase';
import '../styles/ExecutionJournal.css';

const ExecutionJournal = ({ isOpen, onClose, user, userGoal, successCriteria, onDailyGoalComplete }) => {
  const [messages, setMessages] = useState([]);
  const [currentMessage, setCurrentMessage] = useState('');
  const [isAITyping, setIsAITyping] = useState(false);
  const [currentSection, setCurrentSection] = useState(null);
  const [executionPlan, setExecutionPlan] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [isConversationComplete, setIsConversationComplete] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Auto-scroll to bottom of messages
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Load chat history and initialize conversation
  useEffect(() => {
    if (!user || !isOpen) return;

    const chatRef = ref(database, `users/${user.uid}/executionChat`);
    const unsubscribe = onValue(chatRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        setMessages(data.messages || []);
        setExecutionPlan(data.executionPlan || null);
        setCurrentSection(data.currentSection || null);
        setIsInitialized(data.initialized || false);
      }
      
      // Initialize conversation if not already done
      if (!data?.initialized && successCriteria) {
        initializeConversation();
      }
    });

    return () => unsubscribe();
  }, [user, isOpen, successCriteria]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => {
        inputRef.current.focus();
      }, 300);
    }
  }, [isOpen]);

  const initializeConversation = async () => {
    if (!successCriteria || isInitialized) return;

    setIsAITyping(true);
    
    // Generate execution plan based on success criteria
    const plan = await generateExecutionPlan(successCriteria, userGoal);
    
         const welcomeMessage = {
       id: Date.now(),
       type: 'ai',
       content: `# 🎯 Welcome to your Execution Coach!

I've analyzed your target outcome:
> "${successCriteria}"

I've broken this down into **${plan.sections.length} key sections** that we'll work through together:

${plan.sections.map((section, index) => `## ${index + 1}. ${section.title}
${section.description}

**Key Focus Areas:**
${section.tasks.map(task => `- ${task}`).join('\n')}`).join('\n\n')}

---

## Let's Start with: ${plan.sections[0].title}

${plan.sections[0].guidance}

**What's your initial approach to this section?** I'm here to help you refine and execute it step by step. 💪`,
       timestamp: Date.now()
     };

    const initialData = {
      messages: [welcomeMessage],
      executionPlan: plan,
      currentSection: 0,
      initialized: true,
      lastUpdated: Date.now()
    };

    try {
      await set(ref(database, `users/${user.uid}/executionChat`), initialData);
      setIsInitialized(true);
    } catch (error) {
      console.error('Error initializing conversation:', error);
    }
    
    setIsAITyping(false);
  };

  const generateExecutionPlan = async (criteria, goal) => {
    // AI logic to break down the success criteria into actionable sections
    const sections = [];
    
    if (criteria.toLowerCase().includes('business') || criteria.toLowerCase().includes('strategy')) {
      sections.push(
        {
          title: "Research & Analysis",
          description: "Market research, competitor analysis, and opportunity identification",
          guidance: "Let's start by understanding your market. What industry are you targeting, and who do you see as your main competitors?",
          tasks: ["Market research", "Competitor analysis", "Target audience definition"]
        },
        {
          title: "Strategy Development", 
          description: "Core business model, value proposition, and strategic positioning",
          guidance: "Now let's define your unique value proposition. What makes your solution different from existing alternatives?",
          tasks: ["Value proposition", "Business model", "Revenue streams"]
        },
        {
          title: "Implementation Planning",
          description: "Tactical execution, resource allocation, and timeline development",
          guidance: "Time to create your action plan. What are the first 3 concrete steps you need to take to move forward?",
          tasks: ["Action plan", "Resource planning", "Timeline creation"]
        },
        {
          title: "Validation & Launch",
          description: "Testing, feedback collection, and go-to-market execution",
          guidance: "Let's plan how you'll validate your strategy and launch effectively. Who will be your first customers?",
          tasks: ["Testing strategy", "Launch plan", "Success metrics"]
        }
      );
    } else {
      // Generic project breakdown
      sections.push(
        {
          title: "Planning & Preparation",
          description: "Initial research, goal clarification, and resource gathering",
          guidance: "Let's start by clarifying your objectives and gathering what you need. What's your current understanding of the requirements?",
          tasks: ["Requirements gathering", "Resource identification", "Initial planning"]
        },
        {
          title: "Development & Creation",
          description: "Core work execution and iterative development",
          guidance: "Now for the main work. What's your approach to tackling the core deliverables?",
          tasks: ["Core development", "Iterative improvement", "Quality assurance"]
        },
        {
          title: "Review & Refinement",
          description: "Quality check, feedback incorporation, and optimization",
          guidance: "Let's ensure quality and completeness. How will you review and refine your work?",
          tasks: ["Quality review", "Feedback integration", "Optimization"]
        }
      );
    }

    return {
      sections,
      totalSections: sections.length,
      createdAt: Date.now()
    };
  };

  const markDailyGoalComplete = async () => {
    try {
      const today = new Date().toISOString().split('T')[0];
      
      // Save execution journal completion to daily progress
      await progressAPI.recordTaskCompletion(user.uid, userGoal.id, {
        taskId: `execution-journal-${today}`,
        description: 'Completed execution journal conversation',
        completed: true,
        timestamp: Date.now(),
        date: today,
        executionPlan: executionPlan,
        successCriteria: successCriteria,
        conversationComplete: true
      });

      // Mark daily goal as complete
      await progressAPI.updateDailyProgress(user.uid, today, {
        dailyGoalComplete: true,
        executionJournalComplete: true,
        completedAt: new Date().toISOString(),
        lastUpdated: new Date().toISOString()
      });

      // Set conversation as complete
      setIsConversationComplete(true);

      // Notify parent component if callback provided
      if (onDailyGoalComplete) {
        onDailyGoalComplete({
          date: today,
          executionPlan: executionPlan,
          conversationComplete: true
        });
      }

      console.log('Daily goal marked as complete for', today);
    } catch (error) {
      console.error('Error marking daily goal complete:', error);
    }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!currentMessage.trim() || isAITyping) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: currentMessage.trim(),
      timestamp: Date.now()
    };

    // Add user message immediately
    const updatedMessages = [...messages, userMessage];
    setMessages(updatedMessages);
    setCurrentMessage('');
    setIsAITyping(true);

    // Generate AI response
    setTimeout(async () => {
      const aiResponse = await generateAIResponse(currentMessage, updatedMessages, executionPlan, currentSection);
      
      const aiMessage = {
        id: Date.now() + 1,
        type: 'ai',
        content: aiResponse.content,
        timestamp: Date.now()
      };

      const finalMessages = [...updatedMessages, aiMessage];
      
      // Update Firebase
      try {
        const updateData = {
          messages: finalMessages,
          currentSection: aiResponse.newSection !== undefined ? aiResponse.newSection : currentSection,
          lastUpdated: Date.now()
        };

        // If conversation is complete, add completion data
        if (aiResponse.isComplete) {
          updateData.conversationComplete = true;
          updateData.completedAt = Date.now();
          updateData.savedToDate = new Date().toISOString().split('T')[0];
        }

        await update(ref(database, `users/${user.uid}/executionChat`), updateData);
        
        if (aiResponse.newSection !== undefined) {
          setCurrentSection(aiResponse.newSection);
        }

        // Handle conversation completion
        if (aiResponse.isComplete) {
          setIsConversationComplete(true);
        }
      } catch (error) {
        console.error('Error updating chat:', error);
      }
      
      setIsAITyping(false);
    }, 1500);
  };

  const generateAIResponse = async (userInput, messageHistory, plan, sectionIndex) => {
    if (!plan || sectionIndex === null) {
      return {
        content: "I'm here to help you execute your plan. Let me know what you'd like to work on!",
        newSection: sectionIndex
      };
    }

    const currentSectionData = plan.sections[sectionIndex];
    const isComplete = userInput.toLowerCase().includes('done') || 
                      userInput.toLowerCase().includes('complete') || 
                      userInput.toLowerCase().includes('finished');

         if (isComplete && sectionIndex < plan.sections.length - 1) {
       // Move to next section
       const nextSection = plan.sections[sectionIndex + 1];
       return {
         content: `# 🎉 Excellent Progress!

✅ **${currentSectionData.title}** - **COMPLETED**

---

## Next Up: ${nextSection.title}

${nextSection.description}

**Key Focus Areas:**
${nextSection.tasks.map(task => `- ${task}`).join('\n')}

### Guidance:
${nextSection.guidance}

**Ready to dive in?** 🚀`,
         newSection: sectionIndex + 1
       };
     } else if (isComplete && sectionIndex === plan.sections.length - 1) {
       // Final completion - mark daily goal as complete
       await markDailyGoalComplete();
       
       return {
         content: `# 🎊 Congratulations!

## 🏆 Mission Accomplished!

You've successfully completed **all sections** of your execution plan! You've made incredible progress toward:

> "${successCriteria}"

## Your Achievements:
${plan.sections.map((section, i) => `✅ **${section.title}**`).join('\n')}

---

### ✅ **Daily Goal Completed!**
Your execution session has been saved to today's date and your daily goal has been marked as complete.

### 🌟 You should be proud of this achievement!

**What's your next big goal?** I'm ready to help you tackle whatever comes next! 💪`,
         newSection: sectionIndex,
         isComplete: true
       };
     }

         // Generate contextual response based on current section and user input
     const randomTask = currentSectionData.tasks[Math.floor(Math.random() * currentSectionData.tasks.length)];
     const responses = [
       `## 💡 Great Approach!

That's excellent thinking for **${currentSectionData.title}**! 

### Consider This:
- **${randomTask}**

**How does that align with your current thinking?** I'd love to hear your perspective on this aspect.`,
       
       `## 🎯 I Like Your Direction!

Your approach to **${currentSectionData.title}** is solid! 

### To Strengthen This Further:
What if we also explored **${randomTask}**?

**What are your thoughts on incorporating this element?**`,
       
       `## ✅ Solid Thinking!

You're on the right track with **${currentSectionData.title}**. Let's make sure we're covering all the bases.

### Have You Considered:
- **${randomTask}**

**How would you approach this aspect?** I'm curious about your strategy here.`,
       
       `## 🚀 Great Insight!

Building on your thinking, let's dive deeper into this area of **${currentSectionData.title}**.

### Let's Explore:
**${randomTask}**

**What's your plan for tackling this specific aspect?** I think this could be a key component.`,
       
       `## 👍 Excellent Progress!

You're making **great progress** on **${currentSectionData.title}**! 

### To Round This Out:
Let's also address **${randomTask}**

**What's your approach to this element?** This could really strengthen your overall strategy.`
     ];

    return {
      content: responses[Math.floor(Math.random() * responses.length)],
      newSection: sectionIndex
    };
  };

  if (!isOpen) return null;

  return createPortal(
    <div className={`execution-journal-overlay ${isOpen ? 'open' : ''}`}>
      <div className={`execution-journal-panel ${isOpen ? 'slide-in' : ''}`}>
        <div className="execution-journal-header">
          <div className="header-content">
            <h2>⚡ Execution Coach</h2>
            <p>
              {executionPlan && currentSection !== null ? 
                `Section ${currentSection + 1}/${executionPlan.sections.length}: ${executionPlan.sections[currentSection]?.title}` :
                'AI-powered collaborative execution'
              }
            </p>
          </div>
          <button className="close-journal" onClick={onClose}>
            ✕
          </button>
        </div>

        <div className="execution-chat-container">
          <div className="messages-container">
            {messages.map((message) => (
              <div key={message.id} className={`message ${message.type}`}>
                <div className="message-avatar">
                  {message.type === 'ai' ? '🤖' : '👤'}
                </div>
                <div className="message-content">
                  <div className="message-text">
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </div>
                  <div className="message-time">
                    {new Date(message.timestamp).toLocaleTimeString([], { 
                      hour: '2-digit', 
                      minute: '2-digit' 
                    })}
                  </div>
                </div>
              </div>
            ))}
            
            {isAITyping && (
              <div className="message ai">
                <div className="message-avatar">🤖</div>
                <div className="message-content">
                  <div className="typing-indicator">
                    <div className="typing-dots">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          <form onSubmit={handleSendMessage} className="message-input-form">
            <div className="input-container">
              <input
                ref={inputRef}
                type="text"
                value={currentMessage}
                onChange={(e) => setCurrentMessage(e.target.value)}
                placeholder={
                  executionPlan && currentSection !== null ? 
                    `Share your thoughts on ${executionPlan.sections[currentSection]?.title}...` :
                    "Let's start executing your plan..."
                }
                disabled={isAITyping}
                className="message-input"
              />
              <button 
                type="submit" 
                disabled={!currentMessage.trim() || isAITyping}
                className="send-button"
              >
                ➤
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ExecutionJournal; 